import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Code as CodeIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { folderService } from '../../services/folderService';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { invalidateFolderCache } from '../../hooks/useFolderCache';
import NewFolderModal from './NewFolderModal';
import FolderContextMenu from './FolderContextMenu';

// Styled components for folder items
const FolderItem = styled(Box)(({ theme, selected, level = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  marginLeft: level * 20,
  marginBottom: theme.spacing(0.5),
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  backgroundColor: selected 
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.08)})` 
    : 'transparent',
  border: selected 
    ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` 
    : '1px solid transparent',
  
  '&::before': selected ? {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: '60%',
    borderRadius: '0 2px 2px 0',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
  } : {},
  
  '&:hover': {
    backgroundColor: selected 
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.1)})` 
      : alpha(theme.palette.action.hover, 0.6),
    transform: 'translateX(2px)',
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    '& .folder-actions': {
      opacity: 1,
    }
  }
}));

const FolderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  opacity: 0,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  marginLeft: 'auto',
  gap: theme.spacing(0.5),
  '& .MuiIconButton-root': {
    padding: theme.spacing(0.5),
    borderRadius: 6,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      transform: 'scale(1.1)'
    }
  }
}));

const ColorBadge = styled(Box)(({ theme, color }) => ({
  display: 'none', // Hide color badges for professional look
}));

const FolderTree = ({ onFolderSelect, selectedFolderId: externalSelectedFolderId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // State management
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(externalSelectedFolderId || null);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']));
  const [folderSnippets, setFolderSnippets] = useState({}); // Store snippets by folderId
  const [loadingSnippets, setLoadingSnippets] = useState(new Set()); // Track which folders are loading snippets
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState(null);

  // Fetch folder tree
  const fetchFolders = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setFoldersLoading(true);
    setFoldersError(null);
    
    try {
      const response = await folderService.getFolderTree();
      const folderData = response.data || [];
      
      if (folderData.length > 0) {
        // We got real data
        setFolders(folderData);
      } else {
        // No real data, use mock data
        setFolders([
          {
            id: '1',
            folderId: '1', // Add both for compatibility
            name: 'JavaScript',
            color: '#f7df1e',
            parentId: null,
            snippetCount: 15,
            children: [
              {
                id: '2',
                folderId: '2',
                name: 'React',
                color: '#61dafb',
                parentId: '1',
                snippetCount: 8,
                children: [
                  {
                    id: '3',
                    folderId: '3',
                    name: 'Hooks',
                    color: '#61dafb',
                    parentId: '2',
                    snippetCount: 5,
                    children: []
                  }
                ]
              },
              {
                id: '4',
                folderId: '4',
                name: 'Node.js',
                color: '#339933',
                parentId: '1',
                snippetCount: 7,
                children: []
              }
            ]
          },
          {
            id: '5',
            folderId: '5',
            name: 'Python',
            color: '#3776ab',
            parentId: null,
            snippetCount: 12,
            children: []
          }
        ]);
      }
    } catch (err) {
      console.error('❌ Error fetching folders:', err);
      setFoldersError('Failed to load folders');
      
      // Use mock data on error
      setFolders([
        {
          id: '1',
          folderId: '1',
          name: 'JavaScript',
          color: '#f7df1e',
          parentId: null,
          snippetCount: 15,
          children: [
            {
              id: '2',
              folderId: '2',
              name: 'React',
              color: '#61dafb',
              parentId: '1',
              snippetCount: 8,
              children: [
                {
                  id: '3',
                  folderId: '3',
                  name: 'Hooks',
                  color: '#61dafb',
                  parentId: '2',
                  snippetCount: 5,
                  children: []
                }
              ]
            },
            {
              id: '4',
              folderId: '4',
              name: 'Node.js',
              color: '#339933',
              parentId: '1',
              snippetCount: 7,
              children: []
            }
          ]
        },
        {
          id: '5',
          folderId: '5',
          name: 'Python',
          color: '#3776ab',
          parentId: null,
          snippetCount: 12,
          children: []
        }
      ]);
    } finally {
      setFoldersLoading(false);
    }
  }, []); // Remove isAuthenticated dependency to prevent infinite loops

  useEffect(() => {
    if (isAuthenticated) {
      fetchFolders();
    }
  }, [isAuthenticated]); // Only depend on isAuthenticated, not fetchFolders

  // Update selected folder when external prop changes
  useEffect(() => {
    if (externalSelectedFolderId !== undefined) {
      setSelectedFolderId(externalSelectedFolderId);
    }
  }, [externalSelectedFolderId]);

  // Folder operations
  const handleFolderSelect = (folderId) => {
    setSelectedFolderId(folderId);
    if (onFolderSelect) {
      onFolderSelect(folderId);
    }
  };

  const handleFolderToggle = async (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
      
      // ALWAYS fetch snippets when expanding a folder (regardless of snippetCount)
      const existingSnippets = folderSnippets[folderId];
      const hasAlreadyFetched = existingSnippets !== undefined; // Check if we've already tried to fetch
      
      if (!hasAlreadyFetched && user?.userId) {
        // Add to loading state
        setLoadingSnippets(prev => {
          const newSet = new Set([...prev, folderId]);
          return newSet;
        });
        
        try {
          const snippets = await api.snippets.getFolderSnippets(folderId, user.userId);
          
          setFolderSnippets(prev => {
            const newState = {
              ...prev,
              [folderId]: snippets || []
            };
            return newState;
          });
        } catch (error) {
          console.error(`❌ Error fetching snippets for folder ${folderId}:`, error);
          console.error(`❌ Error details:`, {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
          });
          // Still set empty array to mark as "fetched" so we don't try again
          setFolderSnippets(prev => ({
            ...prev,
            [folderId]: []
          }));
        } finally {
          // Remove from loading state
          setLoadingSnippets(prev => {
            const newSet = new Set(prev);
            newSet.delete(folderId);
            return newSet;
          });
        }
      }
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async (folderData) => {
    try {
      const backendFolderData = {
        name: folderData.name,
        parentFolderId: folderData.parentId,
        color: folderData.color,
        description: folderData.description || null
      };
      
      await folderService.createFolder(backendFolderData);
      setShowNewFolderModal(false);
      setSelectedParentId(null);
      
      // Invalidate folder cache and reload folders
      invalidateFolderCache();
      await fetchFolders();
    } catch (err) {
      console.error('Error creating folder:', err);
      throw err;
    }
  };

  const handleFolderOperation = async (operation, folderId, data = null) => {
    try {
      switch (operation) {
        case 'rename':
          await folderService.updateFolder(folderId, { name: data.name });
          break;
        case 'delete':
          await folderService.deleteFolder(folderId);
          if (selectedFolderId === folderId) {
            setSelectedFolderId(null);
          }
          // Clear snippets for deleted folder
          setFolderSnippets(prev => {
            const newState = { ...prev };
            delete newState[folderId];
            return newState;
          });
          break;
        case 'move':
          await folderService.moveFolder(folderId, data.parentId);
          break;
        default:
          break;
      }
      
      // Invalidate folder cache and reload folders
      invalidateFolderCache();
      await fetchFolders();
    } catch (err) {
      console.error(`❌ Error ${operation} folder:`, err);
      throw err;
    }
  };

  // Render snippets within a folder
  const renderFolderSnippets = (folderId, level) => {
    const snippets = folderSnippets[folderId];
    const isLoadingSnippets = loadingSnippets.has(folderId);
    const hasBeenFetched = snippets !== undefined;
    
    // Show loading state
    if (isLoadingSnippets) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: `${(level + 1) * 16}px`, // Same level as snippets
            paddingY: 0.5,
            paddingX: 1,
          }}
        >
          <CircularProgress size={12} sx={{ marginRight: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Loading snippets...
          </Typography>
        </Box>
      );
    }
    
    // If we haven't fetched yet, don't show anything (this shouldn't happen with new logic)
    if (!hasBeenFetched) {
      return null;
    }
    
    // Show empty state if no snippets found
    if (snippets.length === 0) {
      return null; // Don't show anything for empty folders
    }

    return snippets.map((snippet) => (
      <Box
        key={snippet.snipId || snippet.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: `${(level + 1) * 16}px`, // Same level as parent folder + 1 level
          paddingY: 0.25,
          paddingX: 1,
          cursor: 'pointer',
          borderRadius: 4,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.action.hover, 0.5)
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/snippet/${snippet.snipId || snippet.id}`);
        }}
      >
        <CodeIcon sx={{ 
          fontSize: 14, // Slightly smaller than folder icons
          marginRight: 1, 
          color: '#007AFF', // Blue like macOS document icons
          opacity: 0.8
        }} />
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.8rem', // Slightly smaller than folder names
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            fontWeight: 400 // Normal weight, not bold like folders
          }}
        >
          {snippet.title || 'Untitled Snippet'}
        </Typography>
      </Box>
    ));
  };

  const handleContextMenu = (event, folder) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      folder
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleAddSubfolder = (parentId) => {
    setSelectedParentId(parentId);
    setShowNewFolderModal(true);
    handleCloseContextMenu();
  };

  const handleNewSnippetInFolder = (folderId) => {
    if (onFolderSelect) {
      onFolderSelect(folderId);
    }
    
    // Navigate to the home page (snippet creation page) 
    navigate('/');
    handleCloseContextMenu();
  };

  const renderFolders = (folders = [], level = 0) => {
    return folders.map((folder) => {
      const folderId = folder.folderId || folder.id;
      const isExpanded = expandedFolders.has(folderId);
      const isSelected = selectedFolderId === folderId;
      const hasChildFolders = folder.children && folder.children.length > 0;
      const hasSnippets = folder.snippetCount > 0;
      
      // ALWAYS allow expansion - we'll fetch snippets on demand
      const hasChildren = hasChildFolders || true; // Always expandable to check for snippets
      const isLoadingThisFolder = loadingSnippets.has(folderId);

      return (
        <Box key={folderId}>
          <FolderItem
            selected={isSelected}
            level={level}
            onClick={(e) => {
              e.stopPropagation();
              handleFolderSelect(folderId);
            }}
            onContextMenu={(e) => handleContextMenu(e, folder)}
          >
            {/* Expand/Collapse Button - Always show for non-leaf folders */}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleFolderToggle(folderId);
              }}
              sx={{ 
                mr: 0.5, 
                opacity: hasChildren ? 1 : 0.3,
                cursor: hasChildren ? 'pointer' : 'default'
              }}
              disabled={!hasChildren}
            >
              {isLoadingThisFolder ? (
                <CircularProgress size={16} />
              ) : hasChildren && isExpanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : hasChildren ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <Box sx={{ width: 20, height: 20 }} /> // Placeholder for alignment
              )}
            </IconButton>

            {/* Folder Icon */}
            {isExpanded && hasChildFolders ? (
              <FolderOpenIcon 
                sx={{ 
                  mr: 1, 
                  color: folder.color || 'text.secondary',
                  fontSize: '1.1rem'
                }} 
              />
            ) : (
              <FolderIcon 
                sx={{ 
                  mr: 1, 
                  color: folder.color || 'text.secondary',
                  fontSize: '1.1rem'
                }} 
              />
            )}

            {/* Folder Name */}
            <Typography 
              variant="body2" 
              sx={{ 
                flex: 1, 
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? 'primary.main' : 'text.primary'
              }}
            >
              {folder.name}
            </Typography>

            {/* Snippet Count - Show if available */}
            {folder.snippetCount > 0 && (
              <Chip
                label={folder.snippetCount}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: '0.7rem',
                  mr: 1,
                  '& .MuiChip-label': {
                    px: 0.5
                  }
                }}
              />
            )}

            {/* Show loading indicator if fetching snippets */}
            {isLoadingThisFolder && (
              <CircularProgress size={12} sx={{ mr: 1 }} />
            )}

            {/* Folder Actions */}
            <FolderActions className="folder-actions">
              <Tooltip title="More options" placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e, folder);
                  }}
                  sx={{ 
                    p: 0.25,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.action.hover, 0.5)
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </FolderActions>
          </FolderItem>

          {/* Children and Snippets - Show when expanded */}
          {isExpanded && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {/* Render child folders first */}
              {hasChildFolders && renderFolders(folder.children, level + 1)}
              
              {/* Always render snippets section when expanded */}
              {renderFolderSnippets(folderId, level)}
            </Collapse>
          )}
        </Box>
      );
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden'
    }}>
      {/* Folder Section Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 0.5,
        px: 1,
        flexShrink: 0
      }}>
        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          FOLDERS
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <IconButton 
            size="small" 
            onClick={fetchFolders}
            disabled={foldersLoading}
            title="Refresh folders"
            sx={{ p: 0.5 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => {
              setSelectedParentId(null);
              setShowNewFolderModal(true);
            }}
            title="New folder"
            sx={{ p: 0.5 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Folder Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto', 
        px: 1,
        minHeight: 0,
        maxHeight: 'calc(100vh - 280px)'
      }}>
        {foldersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={16} />
          </Box>
        ) : foldersError ? (
          <Alert severity="error" size="small" sx={{ mb: 0.5, py: 0.5 }}>
            <Typography variant="caption">{foldersError}</Typography>
          </Alert>
        ) : folders.length === 0 ? (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              No folders yet
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedParentId(null);
                setShowNewFolderModal(true);
              }}
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              Create Folder
            </Button>
          </Box>
        ) : (
          <Box>
            {renderFolders(folders)}
          </Box>
        )}
      </Box>

      {/* Modals */}
      <NewFolderModal
        open={showNewFolderModal}
        onClose={() => {
          setShowNewFolderModal(false);
          setSelectedParentId(null);
        }}
        onSubmit={handleCreateFolder}
        folders={folders}
        defaultParentId={selectedParentId}
      />

      <FolderContextMenu
        folder={contextMenu?.folder}
        contextMenu={contextMenu}
        onClose={handleCloseContextMenu}
        onOperation={handleFolderOperation}
        onAddSubfolder={handleAddSubfolder}
        onNewSnippet={handleNewSnippetInFolder}
        folders={folders}
      />
    </Box>
  );
};

export default FolderTree;
